import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Outlet, useParams } from "react-router-dom"
import { GetSystemViewDocument, type GetSystemViewQuery, type System } from "@hydrologiq/gql-experience/graphql"
import { useGraphQLClient } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { isSectionVisible } from "@hydrologiq/shared/hooks/sectionVisibility"
import { useSectionReentry } from "@hydrologiq/shared/hooks/useSectionReentry"
import { SystemBasic, AssetBasic, InstallationBasic } from "../types/SystemTypes"
import { SystemViewData, SystemInstallationHeadlines, SystemNode, NodeAlertSummary } from "../types/SystemView"
import AlertsDialog from "@hydrologiq/shared/components/alerts/AlertsDialog"
import { AlertWithSource } from "@hydrologiq/shared/hooks/useAssetAlerts"
import { type SupabaseClient } from "@supabase/supabase-js"
import { getSystemsSupabaseClient } from "../lib/supabase"
import { useSystemRealtime, type RealtimeConnectionStatus, type SystemNodeRow, type SystemConnectionRow } from "../hooks/useSystemRealtime"
import { type Node, type Edge, type OnNodesChange, type OnEdgesChange, applyNodeChanges, applyEdgeChanges } from "@xyflow/react"
import React from "react"
import { createLoadPrecedenceStore, LoadPrecedenceStoreContext, pushLoadPrecedence } from "./loadPrecedenceStore"

interface AlertDialogData {
  alerts: AlertWithSource[]
  totalCount: number
  assetId: string
  title: string
  description?: string
}

type ContextState = {
  loading?: boolean
  system?: System
  systemInfo?: SystemBasic
  systemViewData?: SystemViewData
  error?: string
  refetch?: () => void
  updateSystem: (system: Partial<System>) => void
  updateSystemInfo: (info: SystemBasic) => void
  openAlertsDialog: (assetId: string, alerts: NodeAlertSummary, title?: string, description?: string) => void
}

/**
 * Edit mode context — controls whether the system view is in read-only or edit mode.
 * In read-only mode: nodes are not draggable, connections cannot be made/deleted,
 * add/remove asset actions are hidden.
 */
export interface EditModeContextValue {
  isEditMode: boolean
  setEditMode: (value: boolean) => void
}

export const EditModeContext = createContext<EditModeContextValue>({
  isEditMode: false,
  setEditMode: () => {},
})

export const SystemContext = createContext<ContextState | undefined>(undefined)

/**
 * Supabase client context — provides the authenticated Supabase client
 * to downstream hooks (useSystemRealtime, position persistence, connections).
 */
export const SupabaseContext = createContext<SupabaseClient | null>(null)

/**
 * Realtime context — exposes the realtime connection status and the
 * addRecentWrite deduplication function to downstream components (drag handlers,
 * connection handlers). Task 8 will wire the actual state callbacks.
 */
export interface RealtimeContextValue {
  status: RealtimeConnectionStatus
  addRecentWrite: (id: string) => void
  markDragging: (nodeId: string) => void
  clearDragging: (nodeId: string) => void
}

export const RealtimeContext = createContext<RealtimeContextValue>({
  status: 'disconnected',
  addRecentWrite: () => {},
  markDragging: () => {},
  clearDragging: () => {},
})

/**
 * Nodes/Edges context — lifts ReactFlow nodes and edges state into the provider
 * so the realtime subscription callbacks can dispatch updates to the same state
 * that ApiFlow renders. ApiFlow becomes a controlled component consuming this context.
 */
export interface NodesEdgesContextValue {
  nodes: Node[]
  edges: Edge[]
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  /**
   * The systemViewData snapshot already applied to the canvas. Lives here rather than
   * in ApiFlow because ApiFlow unmounts on sub-tab navigation while this state does not —
   * a component-local ref resets to null on remount and cannot serve this purpose.
   */
  appliedSnapshotRef: React.MutableRefObject<SystemViewData | null>
}

export const NodesEdgesContext = createContext<NodesEdgesContextValue>({
  nodes: [],
  edges: [],
  setNodes: () => {},
  setEdges: () => {},
  onNodesChange: () => {},
  onEdgesChange: () => {},
  appliedSnapshotRef: { current: null },
})

/**
 * Headlines store — updates headlines without causing SystemProvider to re-render.
 * Only components that subscribe via useSystemHeadlines() will re-render.
 */
type HeadlinesListener = () => void

function createHeadlinesStore() {
  let current: SystemInstallationHeadlines | null | undefined = undefined
  const listeners = new Set<HeadlinesListener>()

  return {
    get: () => current,
    set: (value: SystemInstallationHeadlines | null | undefined) => {
      current = value
      listeners.forEach(l => l())
    },
    subscribe: (listener: HeadlinesListener) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}

export const HeadlinesStoreContext = createContext<ReturnType<typeof createHeadlinesStore> | null>(null)

/**
 * Live values store — holds per-(asset,field) liveValue resolved from TimescaleDB
 * by the backend when integration_management is enabled. Polled every 45s.
 * Individual attribute components subscribe via useLiveValue() — only THAT leaf
 * component re-renders, never the parent node or the ReactFlow canvas.
 */
type LiveValuesListener = () => void
type LiveValuesMap = Record<string, number | null | undefined>

function createLiveValuesStore() {
  let current: LiveValuesMap = {}
  const listeners = new Set<LiveValuesListener>()

  return {
    get: () => current,
    set: (values: LiveValuesMap) => {
      current = values
      listeners.forEach(l => l())
    },
    subscribe: (listener: LiveValuesListener) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}

export const LiveValuesStoreContext = createContext<ReturnType<typeof createLiveValuesStore> | null>(null)

/**
 * Node alerts store — holds per-asset alert summaries, updated every 45s.
 * Individual alert badge components subscribe via useNodeAlerts(assetId).
 */
type NodeAlertsMap = Record<string, NodeAlertSummary>

function createNodeAlertsStore() {
  let current: NodeAlertsMap = {}
  const listeners = new Set<() => void>()

  return {
    get: () => current,
    set: (values: NodeAlertsMap) => {
      current = values
      listeners.forEach(l => l())
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}

export const NodeAlertsStoreContext = createContext<ReturnType<typeof createNodeAlertsStore> | null>(null)

/**
 * Attribute values store — holds per-(asset, attributeKey) resolved values from
 * the `asset_attribute` table. Updated every 45s alongside live values and alerts.
 * Individual attribute components subscribe via useLiveAttributeValue().
 */
type AttributeValuesMap = Record<string, string | null>

function createAttributeValuesStore() {
  let current: AttributeValuesMap = {}
  const listeners = new Set<() => void>()

  return {
    get: () => current,
    set: (values: AttributeValuesMap) => {
      current = values
      listeners.forEach(l => l())
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}

export const AttributeValuesStoreContext = createContext<ReturnType<typeof createAttributeValuesStore> | null>(null)

/**
 * Extract liveValue from all node attributes in a system view response and
 * push them into the live values store. Key format: "assetId:field".
 * This runs outside of React state so it never triggers node re-renders.
 */
function _extractAndPushLiveValues(
  dataView: SystemViewData,
  store: ReturnType<typeof createLiveValuesStore>,
) {
  const values: LiveValuesMap = {}
  const allNodes: SystemNode[] = [
    ...dataView.installations.flatMap((g) => g.nodes),
    ...dataView.isolatedNodes,
  ]
  let hasAny = false
  for (const node of allNodes) {
    const assetId = node.asset?.id
    if (!assetId) continue
    const attrs = Array.isArray(node.attributes)
      ? node.attributes
      : typeof node.attributes === "string"
        ? JSON.parse(node.attributes)
        : []
    for (const attr of attrs) {
      if (!attr.field) continue
      if ("liveValue" in attr) {
        values[`${assetId}:${attr.field}`] = attr.liveValue ?? null
        hasAny = true
      }
    }
  }
  // Only push if the backend provided liveValues (integration_management enabled).
  // If no liveValue keys found at all, leave the store empty so the frontend knows
  // to fall back to InfluxDB polling.
  if (hasAny) {
    store.set(values)
  }
}

/**
 * Extract node alerts from a system view response and push to the alerts store.
 */
function _extractAndPushNodeAlerts(
  dataView: SystemViewData,
  store: ReturnType<typeof createNodeAlertsStore>,
) {
  const alerts: NodeAlertsMap = {}
  const allNodes: SystemNode[] = [
    ...dataView.installations.flatMap((g) => g.nodes),
    ...dataView.isolatedNodes,
  ]
  for (const node of allNodes) {
    const assetId = node.asset?.id
    if (!assetId || !node.alerts) continue
    alerts[assetId] = node.alerts
  }
  store.set(alerts)
}

/**
 * Extract resolved attribute values from attribute-keyed node attributes and
 * push to the attribute values store. Key format: "assetId:attr:attributeKey".
 * Covers:
 *  - custom attributes (e.g. "production") resolved server-side from the
 *    `asset_attribute` table — stored from `attr.value`
 *  - "status" — stored from the live `asset.status` so status changes
 *    (e.g. "outbound" → "deployed") refresh on the 45s poll
 *  - "location" — stored from the live `asset.location.name`
 */
function _extractAndPushAttributeValues(
  dataView: SystemViewData,
  store: ReturnType<typeof createAttributeValuesStore>,
) {
  const values: AttributeValuesMap = {}
  const allNodes: SystemNode[] = [
    ...dataView.installations.flatMap((g) => g.nodes),
    ...dataView.isolatedNodes,
  ]
  let hasAny = false
  for (const node of allNodes) {
    const assetId = node.asset?.id
    if (!assetId) continue
    const attrs = Array.isArray(node.attributes)
      ? node.attributes
      : typeof node.attributes === "string"
        ? JSON.parse(node.attributes)
        : []
    for (const attr of attrs) {
      const attrKey = attr.attribute
      if (!attrKey) continue
      if (attrKey === "status") {
        // Frontend renders status text/colour, but the raw asset.status must be
        // refreshed on each poll so e.g. "outbound" → "deployed" reflects live.
        values[`${assetId}:attr:status`] = node.asset?.status ?? null
      } else if (attrKey === "location") {
        values[`${assetId}:attr:location`] = node.asset?.location?.name ?? null
      } else {
        values[`${assetId}:attr:${attrKey}`] = attr.value ?? null
      }
      hasAny = true
    }
  }
  if (hasAny) {
    store.set(values)
  }
}

export const SystemProvider = ({ children }: { children?: React.ReactNode }) => {
  const { systemId } = useParams()
  const [system, setSystem] = useState<System | undefined>(undefined)
  const [systemInfo, setSystemInfo] = useState<SystemBasic | undefined>(undefined)
  const [systemViewData, setSystemViewData] = useState<SystemViewData | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [alertDialogOpen, setAlertDialogOpen] = useState(false)
  const [alertDialogData, setAlertDialogData] = useState<AlertDialogData | null>(null)

  const headlinesStoreRef = useRef(createHeadlinesStore())
  const headlinesStore = headlinesStoreRef.current

  const liveValuesStoreRef = useRef(createLiveValuesStore())
  const liveValuesStore = liveValuesStoreRef.current

  const nodeAlertsStoreRef = useRef(createNodeAlertsStore())
  const nodeAlertsStore = nodeAlertsStoreRef.current

  const attributeValuesStoreRef = useRef(createAttributeValuesStore())
  const attributeValuesStore = attributeValuesStoreRef.current

  const loadPrecedenceStoreRef = useRef(createLoadPrecedenceStore())
  const sentinelRef = useRef<HTMLSpanElement>(null)
  const missedPollRef = useRef(false)
  const pollRef = useRef<(() => void) | undefined>(undefined)
  const loadPrecedenceStore = loadPrecedenceStoreRef.current

  // ─── Lifted nodes/edges state (shared with ApiFlow via NodesEdgesContext) ────
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const appliedSnapshotRef = useRef<SystemViewData | null>(null)

  // Track which nodes are currently being dragged — skip realtime position updates for these
  const draggingNodeIdsRef = useRef<Set<string>>(new Set())

  const markDragging = useCallback((nodeId: string) => {
    draggingNodeIdsRef.current.add(nodeId)
  }, [])

  const clearDragging = useCallback((nodeId: string) => {
    draggingNodeIdsRef.current.delete(nodeId)
  }, [])

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  )

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  )

  // Supabase client — created once and cached via getSystemsSupabaseClient.
  // The getToken function reads the latest bearer token from localStorage
  // (same pattern used by DualGraphQLProvider and RestApiProvider).
  const supabaseClient = useMemo(
    () => getSystemsSupabaseClient(() => localStorage.getItem("bearer_token")),
    []
  )

  // Derive tenantId from loaded system view data (available after initial fetch).
  const tenantId = systemViewData?.system?.tenant ?? ""

  // ─── Realtime subscription ─────────────────────────────────────────────────
  // Wire the realtime hook with callbacks that update the lifted nodes/edges state.
  // - Node INSERT/DELETE: trigger a refetch (full transform re-runs with new data)
  // - Node UPDATE (position): directly update the node's position in state
  // - Connection INSERT: add a minimal edge to edges state
  // - Connection DELETE: remove edge from state
  const refetchRef = useRef<(() => void) | null>(null)

  const onNodeInsert = useCallback((_node: SystemNodeRow) => {
    // Node INSERT comes from createAssetAssignment (GraphQL). The node needs a full
    // transform (attributes, type, etc.) which only the initial systemData transform
    // can provide. Trigger a refetch so the full getSystemView response re-runs.
    refetchRef.current?.()
  }, [])

  const onNodeUpdate = useCallback((updated: Partial<SystemNodeRow> & { id: string }) => {
    // Skip position updates for nodes currently being dragged by this client
    if (draggingNodeIdsRef.current.has(updated.id)) return

    // Position update from another client — directly patch the node's position.
    if (updated.position_x != null && updated.position_y != null) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== updated.id) return node
          // If the node has a parent (inside a group), the stored position is absolute
          // but ReactFlow position is relative to parent. We need to subtract the parent's
          // position to get the correct relative coordinates.
          if (node.parentId) {
            const parent = nds.find((n) => n.id === node.parentId)
            if (parent) {
              return {
                ...node,
                position: {
                  x: updated.position_x! - parent.position.x,
                  y: updated.position_y! - parent.position.y,
                },
              }
            }
          }
          return {
            ...node,
            position: {
              x: updated.position_x!,
              y: updated.position_y!,
            },
          }
        })
      )
    }
  }, [])

  const onNodeDelete = useCallback((nodeId: string) => {
    // Remove node from state and also remove any edges connected to it
    setNodes((nds) => nds.filter((n) => n.id !== nodeId))
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
  }, [])

  const onConnectionInsert = useCallback((connection: SystemConnectionRow) => {
    // Add a minimal ReactFlow edge derived from the system_connection row
    const newEdge: Edge = {
      id: connection.id,
      source: connection.source_node,
      target: connection.target_node,
      sourceHandle: connection.source_handle ?? undefined,
      targetHandle: connection.target_handle ?? undefined,
    }
    setEdges((eds) => {
      // Prevent duplicate edges (in case the optimistic update already added it)
      if (eds.some((e) => e.id === connection.id)) return eds
      return [...eds, newEdge]
    })
  }, [])

  const onConnectionDelete = useCallback((connectionId: string) => {
    setEdges((eds) => eds.filter((e) => e.id !== connectionId))
  }, [])

  const { status: realtimeStatus, addRecentWrite } = useSystemRealtime({
    systemId: systemId ?? "",
    tenantId,
    supabase: supabaseClient,
    onNodeInsert,
    onNodeUpdate,
    onNodeDelete,
    onConnectionInsert,
    onConnectionDelete,
  })

  const realtimeContextValue = useMemo<RealtimeContextValue>(
    () => ({ status: realtimeStatus, addRecentWrite, markDragging, clearDragging }),
    [realtimeStatus, addRecentWrite, markDragging, clearDragging]
  )

  const client = useGraphQLClient("primary")
  const clientRef = useRef(client)
  clientRef.current = client

  const setSystemData = useCallback((data: GetSystemViewQuery): void => {
    if (!data.systemView || !systemId) return
    const dataView = data.systemView as SystemViewData

    setSystem({ name: dataView.system.name, id: systemId, source: dataView.system.source ?? "timescale" })

    const { installationHeadlines, ...structuralData } = dataView
    setSystemViewData(structuralData as SystemViewData)
    headlinesStore.set(installationHeadlines)

    // Extract live values from node attributes and push to the store (no re-render)
    _extractAndPushLiveValues(dataView, liveValuesStore)

    // Extract node alerts and push to the store (no re-render)
    _extractAndPushNodeAlerts(dataView, nodeAlertsStore)

    // Extract attribute values and push to the store (no re-render)
    _extractAndPushAttributeValues(dataView, attributeValuesStore)

    // Load Precedence rows follow each bucket, so they refresh with the poll too
    pushLoadPrecedence(dataView, loadPrecedenceStore)

    let assetNodes = dataView.installations
      .flatMap((installation) => installation.nodes)
      .filter((node) => node.asset)
    assetNodes = assetNodes.concat(dataView.isolatedNodes.filter((node) => node.asset))

    setSystemInfo({
      name: dataView.system.name,
      inlineMonitoring: dataView.installations.length > 1,
      grouped: dataView.installations.length >= 1,
      installations: dataView.installations.reduce((acc, installation) => {
        acc[installation.installation.id] = {
          name: installation.installation.name,
          startDate: installation.installation.start_date,
          endDate: installation.installation.end_date,
        }
        return acc
      }, {} as Record<string, InstallationBasic>),
      assets: assetNodes.reduce((acc, node) => {
        if (node.asset) {
          acc[node.asset.id] = {
            name: node.asset.name,
            location: node.asset.location,
          }
        }
        return acc
      }, {} as Record<string, AssetBasic>),
      graphs: dataView?.system?.graphs,
      figures: dataView?.system?.figures,
      reporting: dataView?.system?.reporting,
    })
  }, [systemId, headlinesStore, liveValuesStore, nodeAlertsStore, attributeValuesStore])

  // Initial fetch
  useEffect(() => {
    if (!systemId) {
      setSystem(undefined)
      setSystemInfo(undefined)
      setSystemViewData(undefined)
      headlinesStore.set(undefined)
      setError(undefined)
      setLoading(false)
      return
    }

    setLoading(true)
    clientRef.current.query({
      query: GetSystemViewDocument,
      variables: { systemId },
      fetchPolicy: "network-only",
    })
      .then(({ data }: { data: GetSystemViewQuery }) => {
        if (data && data.systemView) {
          setSystemData(data)
          setError(undefined)
        } else {
          setError("Failed to load system")
        }
      })
      .catch((err: any) => {
        setError(err.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [systemId, setSystemData, headlinesStore])

  // Unified background poll (2 min) — single network request feeds all three stores
  // (headlines, live values, node alerts). No systemViewData setState, so ReactFlow
  // nodes never re-render. Only leaf subscribers (headline figures, attribute text,
  // alert badges) re-render when their specific data changes.
  // Every poll costs TimescaleDB queries, so it stops while the tab is hidden or the
  // hub has hidden this section (it keeps visited sections mounted), and refreshes
  // as soon as the user is back. https://github.com/hydrologiq/core/issues/119
  useEffect(() => {
    if (!systemId) return

    const POLL_INTERVAL = 2 * 60 * 1000
    const poll = () => {
      clientRef.current.query({
        query: GetSystemViewDocument,
        variables: { systemId },
        fetchPolicy: "network-only",
      })
        .then(({ data }: { data: GetSystemViewQuery }) => {
          if (data && data.systemView) {
            const dataView = data.systemView as SystemViewData
            headlinesStore.set(dataView.installationHeadlines)
            _extractAndPushLiveValues(dataView, liveValuesStore)
            _extractAndPushNodeAlerts(dataView, nodeAlertsStore)
            _extractAndPushAttributeValues(dataView, attributeValuesStore)
            pushLoadPrecedence(dataView, loadPrecedenceStore)
          }
        })
        .catch(() => {})
    }
    // The hub gives no "left the section" signal, so check on each tick and note a
    // skipped poll for useSectionReentry below to make up.
    const pollIfShown = () => {
      const sentinel = sentinelRef.current
      if (sentinel && !isSectionVisible(sentinel)) {
        missedPollRef.current = true
        return
      }
      missedPollRef.current = false
      poll()
    }
    pollRef.current = poll

    let intervalId: ReturnType<typeof setInterval> | undefined
    const stop = () => {
      clearInterval(intervalId)
      intervalId = undefined
    }
    const start = () => {
      stop()
      intervalId = setInterval(pollIfShown, POLL_INTERVAL)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        pollIfShown()
        start()
      } else {
        stop()
      }
    }

    if (document.visibilityState === "visible") start()
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      stop()
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [systemId, headlinesStore, liveValuesStore, nodeAlertsStore, attributeValuesStore])

  // Back in the section after a poll was skipped: refresh now, not at the next tick.
  useSectionReentry(() => {
    if (!missedPollRef.current) return
    missedPollRef.current = false
    pollRef.current?.()
  })

  // Manual refetch (e.g. after status toggle) — full reload
  const refetch = useCallback(() => {
    if (systemId) {
      clientRef.current.query({
        query: GetSystemViewDocument,
        variables: { systemId },
        fetchPolicy: "network-only",
      })
        .then(({ data }: { data: GetSystemViewQuery }) => {
          if (data && data.systemView) {
            setSystemData(data)
            setError(undefined)
          }
        })
        .catch((err: any) => {
          setError(err.message)
        })
    }
  }, [systemId, setSystemData])

  // Keep refetchRef in sync so realtime callbacks can trigger a refetch
  refetchRef.current = refetch

  const updateSystem = useCallback((newSystem: Partial<System>) => {
    setSystem(prev => ({ ...prev, ...newSystem } as System))
  }, [])

  const updateSystemInfoData = useCallback((info: SystemBasic) => {
    setSystemInfo(info)
  }, [])

  const openAlertsDialog = useCallback((assetId: string, alerts: NodeAlertSummary, title: string = "Alerts", description?: string) => {
    const alertsWithSource: AlertWithSource[] = alerts.alerts.map(alert => ({
      ...alert,
      source: "asset" as const,
      assetId: assetId,
    }))

    setAlertDialogData({
      alerts: alertsWithSource,
      totalCount: alerts.total,
      assetId,
      title,
      description,
    })
    setAlertDialogOpen(true)
  }, [])

  const value = useMemo(() => ({
    system,
    systemInfo,
    systemViewData,
    loading,
    error,
    refetch,
    updateSystem,
    updateSystemInfo: updateSystemInfoData,
    openAlertsDialog,
  }), [system, systemInfo, systemViewData, loading, error, refetch, updateSystem, updateSystemInfoData, openAlertsDialog])

  const nodesEdgesValue = useMemo<NodesEdgesContextValue>(() => ({
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    appliedSnapshotRef,
  }), [nodes, edges, onNodesChange, onEdgesChange])

  // ─── Edit mode state ─────────────────────────────────────────────────────────
  const [isEditMode, setEditMode] = useState(false)
  const editModeValue = useMemo<EditModeContextValue>(() => ({
    isEditMode,
    setEditMode,
  }), [isEditMode])

  return (
    <SystemContext.Provider value={value}>
      <HeadlinesStoreContext.Provider value={headlinesStore}>
        <LiveValuesStoreContext.Provider value={liveValuesStore}>
          <NodeAlertsStoreContext.Provider value={nodeAlertsStore}>
            <AttributeValuesStoreContext.Provider value={attributeValuesStore}>
            <LoadPrecedenceStoreContext.Provider value={loadPrecedenceStore}>
              <SupabaseContext.Provider value={supabaseClient}>
                <RealtimeContext.Provider value={realtimeContextValue}>
                  <NodesEdgesContext.Provider value={nodesEdgesValue}>
                    <EditModeContext.Provider value={editModeValue}>
                      <span ref={sentinelRef} aria-hidden="true" style={{ position: "absolute", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }} />
                      {children ? children : <Outlet />}
                    
                      {alertDialogData && (
                        <AlertsDialog
                          alerts={alertDialogData.alerts}
                          totalCount={alertDialogData.totalCount}
                          open={alertDialogOpen}
                          onOpenChange={setAlertDialogOpen}
                          assetId={alertDialogData.assetId}
                          refetchQueries={["GetSystemView"]}
                          title={alertDialogData.title}
                          description={alertDialogData.description}
                          updateNote="Updated from system node"
                        />
                      )}
                    </EditModeContext.Provider>
                  </NodesEdgesContext.Provider>
                </RealtimeContext.Provider>
              </SupabaseContext.Provider>
            </LoadPrecedenceStoreContext.Provider>
            </AttributeValuesStoreContext.Provider>
          </NodeAlertsStoreContext.Provider>
        </LiveValuesStoreContext.Provider>
      </HeadlinesStoreContext.Provider>
    </SystemContext.Provider>
  )
}
